<script>
    import { onMount } from "svelte";
    import WindowTitle from "./WindowTitle.svelte";
    import WindowResizeHandle from "./WindowResizeHandle.svelte";
    import windows from "../../stores/windows.js";
    import article from "../../stores/article.js";
    import classifieds from "../../stores/classifieds.js";
    import food from "../../stores/food.js";
    import journal from "../../stores/journal.js";
    import Article from "../content/Article.svelte";
    import Classifieds from "../content/Classifieds.svelte";
    import Info from "../content/Info.svelte";
    import Map from "../content/Map.svelte";
    import Metrics from "../content/Metrics.svelte";
    import Photos from "../content/Photos.svelte";
    import Radio from "../content/Radio.svelte";
    import Vehicle from "../content/Vehicle.svelte";
    import Onboarding from "../content/Onboarding.svelte";
    import Photo from "../content/Photo.svelte";
    import Talkie from "../content/Talkie.svelte";
    import Settings from "../content/Settings.svelte";
    import PagingTools from "../tools/PagingTools.svelte";
    import MapTools from "../tools/MapTools.svelte";
    import ColorPicker from "../content/ColorPicker.svelte";
    import About from "../content/About.svelte";
    import Food from "../content/Food.svelte";
    import Arrival from "../content/Arrival.svelte";
    import Report from "../content/Report.svelte";
    import News from "../content/News.svelte";
    import Cairn from "../content/Cairn.svelte";
    import PhotoTools from "../tools/PhotoTools.svelte";
    import BuildCairn from "../content/BuildCairn.svelte";
    import isMobile from "../../helpers/mobile.js";
    import CairnTools from "../tools/CairnTools.svelte";
    import Journal from "../content/Journal.svelte";

    export let window;

    let innerWidth;
    let titleBar;
    let contentWrapper;

    $: style =
        `width: ${window.width}px;` +
        `height: ${window.height}px;` +
        `left: ${window.x}px;` +
        `top: ${window.y}px;` +
        `z-index: ${window.z || 1}`;

    onMount(() => {
        if (!window.sizeToFit) {
            return;
        }

        function resize() {
            if (!titleBar) {
                setTimeout(resize, 1 / 60);
                return;
            }

            const chromeDepth = 3.0;
            const chromeHeight = chromeDepth * 4.0;
            const height =
                chromeHeight +
                titleBar.clientHeight +
                contentWrapper.firstChild.clientHeight;

            windows.resize(window.id, window.width, height);
        }

        resize();
    });

    function didMouseDown(e) {
        if (e.target.dataset.close) {
            return;
        }

        if (window.isFocused) {
            return;
        }

        windows.focus(window.id);
    }

    function didMouseEnter(e) {
        if (window.isHoverable) {
            windows.hover(window.id);
        }
    }

    function didMouseLeave(e) {
        if (window.isHoverable) {
            windows.unhover(window.id);
        }
    }

    function canResize(window) {
        return (
            !isMobile &&
            !window.isModal &&
            (!window.min ||
                !window.max ||
                window.min.width !== window.max.width ||
                window.min.height !== window.max.height)
        );
    }
</script>

<svelte:window bind:innerWidth />

<!-- svelte-ignore a11y-no-static-element-interactions -->

<div
    class="window"
    class:focused={window.isFocused}
    class:resizing={window.isResizing}
    {style}
    on:mousedown={didMouseDown}
    on:touchstart={didMouseDown}
    on:mouseenter={didMouseEnter}
    on:mouseleave={didMouseLeave}
>
    <div class="title" bind:this={titleBar}>
        <WindowTitle {window} />
    </div>

    {#if window.kind === "article"}
        <div class="toolbar">
            <PagingTools store={article} />
        </div>
    {:else if window.kind === "classifieds"}
        <div class="toolbar">
            <PagingTools store={classifieds} />
        </div>
    {:else if window.kind === "food"}
        <div class="toolbar">
            <PagingTools store={food} />
        </div>
    {:else if window.kind === "map"}
        <div class="toolbar">
            <MapTools {window} />
        </div>
    {:else if window.kind === "photo"}
        <div class="toolbar">
            <PhotoTools {window} />
        </div>
    {:else if window.kind === "cairn"}
        <div class="toolbar">
            <CairnTools {window} />
        </div>
    {:else if window.kind === "journal"}
        <div class="toolbar">
            <PagingTools store={journal} />
        </div>
    {/if}

    <div class="content" bind:this={contentWrapper}>
        {#if window.kind === "article"}
            <Article {window} />
        {:else if window.kind === "info"}
            <Info />
        {:else if window.kind === "metrics"}
            <Metrics />
        {:else if window.kind === "classifieds"}
            <Classifieds {window} />
        {:else if window.kind === "radio"}
            <Radio />
        {:else if window.kind === "map"}
            <Map {window} />
        {:else if window.kind === "vehicle"}
            <Vehicle {window} />
        {:else if window.kind === "photos"}
            <Photos {window} />
        {:else if window.kind === "onboarding"}
            <Onboarding />
        {:else if window.kind === "photo"}
            <Photo {window} />
        {:else if window.kind === "talkie"}
            <Talkie />
        {:else if window.kind === "color"}
            <ColorPicker {window} on:select={window.props.onSelect} />
        {:else if window.kind === "settings"}
            <Settings />
        {:else if window.kind === "about"}
            <About />
        {:else if window.kind === "arrival"}
            <Arrival />
        {:else if window.kind === "news"}
            <News {window} />
        {:else if window.kind === "report"}
            <Report {window} />
        {:else if window.kind === "cairn"}
            <Cairn {window} />
        {:else if window.kind === "food"}
            <Food {window} />
        {:else if window.kind === "build-cairn"}
            <BuildCairn {window} />
        {:else if window.kind === "journal"}
            <Journal {window} />
        {/if}
    </div>

    {#if canResize(window)}
        <WindowResizeHandle {window} />
    {/if}
</div>

<style>
    .window {
        display: flex;
        position: absolute;
        flex-direction: column;
        box-sizing: border-box;
        border-top: var(--border-color-light) var(--chrome-depth) outset;
        border-right: var(--border-color-dark) var(--chrome-depth) outset;
        border-bottom: var(--border-color-dark) var(--chrome-depth) outset;
        border-left: var(--border-color-light) var(--chrome-depth) outset;
        background: var(--chrome-hsl);
        padding: var(--chrome-depth);
    }

    .window.focused {
        box-shadow: calc(var(--chrome-depth) * 2) calc(var(--chrome-depth) * 2)
            rgba(0, 0, 0, 0.5);
    }

    .title {
        flex-shrink: 0;
    }

    .toolbar {
        display: flex;
        flex-shrink: 0;
        flex-direction: row;
        align-items: center;
        padding-bottom: var(--chrome-depth);
        min-height: 0;
    }

    .content {
        flex-grow: 1;
        flex-shrink: 1;
        box-sizing: border-box;
        border-top: var(--border-color-dark) var(--chrome-depth) inset;
        border-right: var(--border-color-light) var(--chrome-depth) inset;
        border-bottom: var(--border-color-light) var(--chrome-depth) inset;
        border-left: var(--border-color-dark) var(--chrome-depth) inset;
        background: var(--chrome-hsl);
        width: 100%;
        min-height: 0;
        pointer-events: none;
        font-size: 13px;
    }

    .focused .content {
        pointer-events: auto;
    }
</style>
